"use client";

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Global open/close state for the command palette plus its keyboard shortcut.
 *
 * Hub had the cmdk primitive (`components/ui/command.tsx`) sitting unused and no
 * keyboard entry point anywhere — every navigation was a mouse trip through a
 * hover menu. This provider owns the ⌘K / Ctrl+K binding so both the hotkey and
 * the visible header button drive the same surface.
 */
interface CommandPaletteContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  openPalette: () => void;
  closePalette: () => void;
  /** `⌘K` on Apple platforms, `Ctrl K` elsewhere — for rendering the hint. */
  shortcutLabel: string;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | undefined>(undefined);

/** True on Apple platforms, where the accelerator is Command rather than Control. */
function detectApplePlatform(): boolean {
  if (typeof navigator === "undefined") return false;
  // `navigator.platform` is deprecated but still the most reliable signal here,
  // and userAgentData is not available in every browser Hub supports.
  const platform = `${navigator.platform ?? ""} ${navigator.userAgent ?? ""}`;
  return /Mac|iPhone|iPad|iPod/i.test(platform);
}

export function CommandPaletteProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  // Resolved after mount: reading `navigator` during render would differ between
  // the server and the client and trip hydration.
  const [isApple, setIsApple] = useState(false);

  useEffect(() => {
    setIsApple(detectApplePlatform());
  }, []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      // Accept either accelerator on every platform: a Mac user on an external
      // PC keyboard and a Windows user both get the shortcut they reach for.
      const isPaletteChord = event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey);
      if (!isPaletteChord) return;
      // Never swallow browser/OS chords that also carry Alt or Shift.
      if (event.altKey || event.shiftKey) return;

      event.preventDefault();
      setOpen((previous) => !previous);
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const openPalette = useCallback(() => setOpen(true), []);
  const closePalette = useCallback(() => setOpen(false), []);

  const value = useMemo<CommandPaletteContextValue>(
    () => ({
      open,
      setOpen,
      openPalette,
      closePalette,
      shortcutLabel: isApple ? "⌘K" : "Ctrl K",
    }),
    [open, openPalette, closePalette, isApple]
  );

  return (
    <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>
  );
}

/**
 * Palette state. Returns null when no provider is mounted (e.g. a boundary page
 * rendered outside the app tree) so callers can degrade instead of crashing —
 * same reasoning as BOUNDARY_PAGES_OPTIONAL_AUTH.
 */
export function useCommandPaletteOptional(): CommandPaletteContextValue | null {
  return useContext(CommandPaletteContext) ?? null;
}

/** Palette state; throws when used outside the provider. */
export function useCommandPalette(): CommandPaletteContextValue {
  const context = useContext(CommandPaletteContext);
  if (!context) {
    throw new Error("useCommandPalette must be used within a CommandPaletteProvider");
  }
  return context;
}
